// Tiny browser-synthesized sound effects. No assets, no network.
let _ctx: AudioContext | null = null;
function ctx(): AudioContext {
  if (!_ctx) {
    const C =
      (window as unknown as { AudioContext?: typeof AudioContext }).AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    _ctx = new C!();
  }
  return _ctx!;
}

// Eagerly unlock the AudioContext on the very first user gesture so we can
// later play the fanfare from a non-gesture context (a useEffect, a timer).
if (typeof window !== "undefined") {
  const unlock = () => {
    try {
      const c = ctx();
      if (c.state === "suspended") c.resume().catch(() => {});
      // Play a brief silent buffer to fully prime the graph.
      const buf = c.createBuffer(1, 1, 22050);
      const src = c.createBufferSource();
      src.buffer = buf;
      src.connect(c.destination);
      src.start(0);
    } catch {
      /* ignore */
    }
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
    window.removeEventListener("touchstart", unlock);
    window.removeEventListener("click", unlock);
  };
  window.addEventListener("pointerdown", unlock, { once: true });
  window.addEventListener("keydown", unlock, { once: true });
  window.addEventListener("touchstart", unlock, { once: true });
  window.addEventListener("click", unlock, { once: true });
}

// Royal "you have been drafted!" trumpet fanfare. Returns a promise that
// resolves when the fanfare has finished playing.
export async function playFanfare(): Promise<void> {
  const c = ctx();
  if (c.state === "suspended") {
    try {
      await c.resume();
    } catch {
      /* ignore */
    }
  }

  // Triumphant G major call: G4 → B4 → D5 → G5 → held G major triad.
  const notes: Array<{ freq: number; start: number; dur: number; held?: boolean }> = [
    { freq: 392.0, start: 0.0, dur: 0.16 },
    { freq: 493.88, start: 0.16, dur: 0.16 },
    { freq: 587.33, start: 0.32, dur: 0.18 },
    { freq: 783.99, start: 0.5, dur: 0.95, held: true },
  ];
  // Stacked harmonized voice on the held note for a chord swell.
  const harmonyOnHeld = [493.88, 587.33]; // B4, D5

  const t0 = c.currentTime + 0.05;

  function brass(start: number, end: number, freq: number, gain = 0.35) {
    // Two oscillators detuned + low-pass for a warmer brass timbre.
    const env = c.createGain();
    env.gain.setValueAtTime(0, start);
    env.gain.linearRampToValueAtTime(gain, start + 0.025);
    env.gain.linearRampToValueAtTime(gain * 0.85, end - 0.06);
    env.gain.linearRampToValueAtTime(0, end);

    const filter = c.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 2400;
    filter.Q.value = 0.7;
    env.connect(filter);
    filter.connect(c.destination);

    const a = c.createOscillator();
    a.type = "sawtooth";
    a.frequency.value = freq;
    const b = c.createOscillator();
    b.type = "sawtooth";
    b.frequency.value = freq * 1.005; // tiny detune for chorus
    const sub = c.createOscillator();
    sub.type = "square";
    sub.frequency.value = freq * 0.5;

    const ga = c.createGain();
    ga.gain.value = 0.55;
    const gb = c.createGain();
    gb.gain.value = 0.45;
    const gs = c.createGain();
    gs.gain.value = 0.25;

    a.connect(ga).connect(env);
    b.connect(gb).connect(env);
    sub.connect(gs).connect(env);

    a.start(start);
    b.start(start);
    sub.start(start);
    a.stop(end + 0.05);
    b.stop(end + 0.05);
    sub.stop(end + 0.05);
  }

  for (const n of notes) {
    brass(t0 + n.start, t0 + n.start + n.dur, n.freq, n.held ? 0.4 : 0.32);
  }
  // Chord stack on the held note.
  for (const f of harmonyOnHeld) {
    const held = notes[notes.length - 1]!;
    brass(t0 + held.start + 0.04, t0 + held.start + held.dur, f, 0.22);
  }

  // Very short snare-roll-ish noise tick at the start for impact.
  {
    const buf = c.createBuffer(1, c.sampleRate * 0.12, c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    }
    const src = c.createBufferSource();
    src.buffer = buf;
    const g = c.createGain();
    g.gain.value = 0.18;
    const hp = c.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 1200;
    src.connect(hp).connect(g).connect(c.destination);
    src.start(t0);
  }

  const totalSec = notes[notes.length - 1]!.start + notes[notes.length - 1]!.dur + 0.2;
  await new Promise((r) => setTimeout(r, totalSec * 1000));
}
